import { getIn, useFormikContext } from "formik"
import { useEffect, useState, useMemo } from "react"
import { useGetFuelTypesQuery, useContextAwareGetDefaultCountry } from "@hooks/index"
import { useGetMacroFuelTypesQuery } from "@app/features/dropdownOptions/dropdownOptionsApiSlice"
import { isEqual } from 'lodash';
import { selectCurrentProject } from "@app/features/project/projectSlice";
import { useAppSelector } from "@app/hooks";

interface VCEnergyHandlerProps {
  listInputName: string,
  energyTypeInputName: string,
}

const ELECTRICITY_IDS = [35]
const ELECTRICITY_NAME = "Electricity"

const RENEWABLE_IDS = [36]
const RENEWABLE_NAME = "Renewable"

// this hook is for:
// - setting the values for fuel units if electricity is selected
// - setting the values for is_electricity_selected and is_solid_fuel_selected

export const useVCEnergyHandler = <T, L>({ listInputName, energyTypeInputName }: VCEnergyHandlerProps) => {
  const { project } = useAppSelector(selectCurrentProject);
  const { values, setFieldValue } = useFormikContext<T>()
  const { data: fuelTypes } = useGetFuelTypesQuery({});
  const { data: macroFuelTypes } = useGetMacroFuelTypesQuery();
  const { data: defaultCountry } = useContextAwareGetDefaultCountry(project?.country ?? 0, { skip: !project?.country, refetchOnMountOrArgChange: true });

  const [list, setList] = useState<L[]>([])
  const [electricityIds, setElectricityIds] = useState<number[]>([])
  const [renewableIds, setRenewableIds] = useState<number[]>([])
  const [solidFuelIds, setSolidFuelIds] = useState<number[]>([])

  useEffect(() => {
    const valuesList: L[] = getIn(values, listInputName)
    const isListUpdated = Array.isArray(valuesList) ? !isEqual(valuesList, list) : false
    if (isListUpdated) { setList([...valuesList]); }
  }, [values, list, listInputName])

  // Build a map of macro fuel type ID to name for efficient lookup
  const macroFuelTypeMap = useMemo(() => {
    if (!macroFuelTypes) return new Map<number, string>();
    return new Map(macroFuelTypes.map(mft => [mft.id, mft.name_en || mft.name]));
  }, [macroFuelTypes]);

  useEffect(() => {
    if (fuelTypes) {
      setSolidFuelIds(fuelTypes.filter(fuelType => fuelType?.macro_fuel_type?.name_en?.startsWith("Solid")).map(fuelType => fuelType.id))
      const mappedElectricityIds = fuelTypes.filter(fuelType => fuelType?.macro_fuel_type?.name_en?.startsWith(ELECTRICITY_NAME)).map(fuelType => fuelType.id)
      const mappedRenewableIds = fuelTypes.filter(fuelType => fuelType?.macro_fuel_type?.name_en?.startsWith(RENEWABLE_NAME)).map(fuelType => fuelType.id)
      setElectricityIds(mappedElectricityIds.length ? mappedElectricityIds : ELECTRICITY_IDS)
      setRenewableIds(mappedRenewableIds.length ? mappedRenewableIds : RENEWABLE_IDS)
    }
  }, [fuelTypes, macroFuelTypes, macroFuelTypeMap])

  //setting the default country
  useEffect(() => {
    if (defaultCountry && list.length > 0) {
      // Update all items in a single batch operation
      list.forEach((_, index) => {
        setFieldValue(`${listInputName}.${index}.country_t2_default`, defaultCountry.name);
      });
    }
  }, [defaultCountry, project, list, listInputName, setFieldValue])

  useEffect(() => {
    for (const [index, item] of list.entries()) {
      const energyTypeStart = getIn(item, `${energyTypeInputName}_start`)
      const energyTypeWith = getIn(item, `${energyTypeInputName}_w`)
      const energyTypeWithout = getIn(item, `${energyTypeInputName}_wo`)

      const isElectricityStart = electricityIds.includes(energyTypeStart)
      const isElectricityWith = electricityIds.includes(energyTypeWith)
      const isElectricityWithout = electricityIds.includes(energyTypeWithout)
      const isRenewableStart = renewableIds.includes(energyTypeStart)
      const isRenewableWith = renewableIds.includes(energyTypeWith)
      const isRenewableWithout = renewableIds.includes(energyTypeWithout)
      const isSolidFuelSelected = [energyTypeStart, energyTypeWith, energyTypeWithout].some(id => solidFuelIds.includes(id))

      const isElectricityStartFlag = isElectricityStart || isRenewableStart
      const isElectricityWithFlag = isElectricityWith || isRenewableWith
      const isElectricityWithoutFlag = isElectricityWithout || isRenewableWithout

      const startUnit = fuelTypes?.find(fuelType => fuelType.id === energyTypeStart)?.unit ?? ""
      const withUnit = fuelTypes?.find(fuelType => fuelType.id === energyTypeWith)?.unit ?? ""
      const withoutUnit = fuelTypes?.find(fuelType => fuelType.id === energyTypeWithout)?.unit ?? ""

      setFieldValue(`${listInputName}.${index}.${energyTypeInputName}_start_unit`, startUnit)
      setFieldValue(`${listInputName}.${index}.${energyTypeInputName}_w_unit`, withUnit)
      setFieldValue(`${listInputName}.${index}.${energyTypeInputName}_wo_unit`, withoutUnit)
      setFieldValue(`${listInputName}.${index}.is_electricity_selected`, isElectricityStartFlag || isElectricityWithFlag || isElectricityWithoutFlag)
      setFieldValue(`${listInputName}.${index}.is_electricity_start`, isElectricityStartFlag)
      setFieldValue(`${listInputName}.${index}.is_electricity_w`, isElectricityWithFlag)
      setFieldValue(`${listInputName}.${index}.is_electricity_wo`, isElectricityWithoutFlag)
      setFieldValue(`${listInputName}.${index}.is_solid_fuel_selected`, isSolidFuelSelected)
    }
  }, [list, electricityIds, solidFuelIds, fuelTypes, listInputName, energyTypeInputName, setFieldValue, renewableIds])
}
